import Cookies from 'js-cookie';
import { getConfig } from '../config';
import type { GdprCookieData, GdprPreferences } from '../types';

/**
 * Check if GDPR cookie exists
 */
export function checkCookie(): boolean {
    const config = getConfig();
    if (!config) return false;
    return Cookies.get(config.name_cookie) !== undefined;
}

/**
 * Get and parse GDPR cookie data
 */
export function getCookie(): GdprCookieData | null {
    const config = getConfig();
    if (!config) return null;

    const raw = Cookies.get(config.name_cookie);
    if (!raw) return null;

    try {
        return JSON.parse(raw) as GdprCookieData;
    } catch {
        return null;
    }
}

/**
 * Remove GDPR cookie
 */
export function removeCookie(): void {
    const config = getConfig();
    if (!config) return;
    Cookies.remove(config.name_cookie);
}

/**
 * Remove cookie if version has changed
 */
export function removeCookieByVersion(): void {
    const config = getConfig();
    if (!config) return;

    const cookie = getCookie();
    if (cookie && cookie.ver !== config.version) {
        removeCookie();
    }
}

/**
 * Get preferences from stored cookie
 */
export function getPreferences(): GdprPreferences {
    const defaults: GdprPreferences = { preference: false, stats: false, marketing: false };
    const cookie = getCookie();
    if (!cookie?.details) return defaults;

    return {
        preference: cookie.details.preference ?? false,
        stats: cookie.details.stats ?? false,
        marketing: cookie.details.marketing ?? false,
    };
}

/**
 * Get checkbox values from DOM by selector
 */
export function getCheckboxValues(selector: string): Array<{ type: string; checked: boolean }> {
    const checkboxes = document.querySelectorAll<HTMLInputElement>(selector);
    return Array.from(checkboxes).map((el) => ({
        type: el.getAttribute('data-type') ?? '',
        checked: el.checked,
    }));
}

/**
 * Set checkbox states from stored preferences
 */
export function setCheckboxesFromPreferences(preferences: GdprPreferences): void {
    const mapping: Record<string, string> = {
        preference: '#gdpr-cookie-set-preferences',
        stats: '#gdpr-cookie-set-statistics',
        marketing: '#gdpr-cookie-set-marketing',
    };

    for (const [key, selector] of Object.entries(mapping)) {
        const checkbox = document.querySelector<HTMLInputElement>(selector);
        if (checkbox) {
            checkbox.checked = preferences[key as keyof GdprPreferences];
        }
    }
}

/**
 * Create loading spinner element
 */
export function btnSpin(): HTMLElement {
    const spin = document.createElement('i');
    spin.setAttribute('id', 'bgdrop-spin');
    spin.setAttribute('class', 'fa-solid fa-circle-notch fa-spin ml-1 mt-1');
    return spin;
}

/**
 * Remove spinner and re-enable button
 */
export function resetButton(spin: HTMLElement, btn: HTMLButtonElement): void {
    spin.remove();
    btn?.removeAttribute('disabled');
}
